//@author: devin

import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output,
    ViewEncapsulation,
    TemplateRef,
    input,
    inject,
    viewChild,
    model,
    OnInit,
} from "@angular/core";
import type { PlacementArray } from "@ng-bootstrap/ng-bootstrap";
import { type MultiselectModelI, S25MultiselectComponent } from "../s25.multiselect.component";
import { S25MultiselectPopupComponent } from "../s25.multiselect.popup.component";
import { SearchCriteriaDataUtil } from "../search.criteria.data.util";
import { SearchCriteriaContext } from "../../../services/search/search-criteria/search.criteria.context";
import type { SearchCriteriaType } from "../../../pojo/SearchCriteriaI";
import type { S25ItemI } from "../../../pojo/S25ItemI";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import type { InferSignalProp } from "../../../pojo/Util";

@TypeManagerDecorator("s25-ng-multiselect-search-criteria")
@Component({
    selector: "s25-ng-multiselect-search-criteria",
    template: `
        @if (isInit && usePopover) {
            <s25-ng-multiselect-popup
                [modelBean]="modelBean"
                [popoverTemplate]="searchCriteriaMultiselect"
                class="{{ !modelBean.isInline ? 'ngRelative' : '' }}"
                [popoverOnBody]="popoverOnBody"
                [popoverPlacement]="popoverPlacement"
                [customPopoverClass]="customPopoverClass"
                [resultsTemplate]="resultsTemplate"
                [disabled]="disabled()"
                [readonly]="readonly()"
                [ariaLabel]="ariaLabel()"
                [ariaRequired]="ariaRequired()"
                [ariaInvalid]="ariaInvalid()"
                [ariaDescribedby]="ariaDescribedby()"
                (close)="onClose()"
                (changed)="emitDone()"
                [(value)]="value"
                (valueChange)="changed.emit(value())"
                [sortResultsBy]="sortResultsBy()"
                [sortResultsOrder]="sortResultsOrder()"
            />
        }
        <ng-template #searchCriteriaMultiselect>
            <s25-ng-multiselect [modelBean]="modelBean" (onDone)="onDone.emit($event)" [(value)]="value" />
        </ng-template>
        @if (isInit && !usePopover) {
            <ng-container [ngTemplateOutlet]="searchCriteriaMultiselect"> </ng-container>
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25MultiselectSearchCriteriaComponent extends S25MultiselectComponent implements OnInit {
    @Input() type: SearchCriteriaType["type"];
    @Input() honorMatching?: boolean = false;
    @Input() customFilterValue: string = "";
    @Input() selectedItems?: S25ItemI[] = [];
    @Input() popoverOnBody = false;
    @Input() popoverPlacement: PlacementArray = "auto";
    @Input() customPopoverClass?: string;
    @Input() useSecurity?: boolean;
    /** Passed as the second arg to non-expensive searchCriteria service methods (e.g. event categories). */
    @Input() eventTypeId?: number;
    @Input() usePopover?: boolean = true;
    /** When true, the multiselect uses radio behavior (one selection). */
    @Input() singleSelect?: boolean;
    /** When non-empty, only these item IDs appear in the list (client-side filter after load). */
    @Input() allowedItemIds?: number[];
    @Input() resultsTemplate: TemplateRef<unknown>;
    @Input() buttonText: string;
    readonly matching = model<MultiselectModelI["matching"]>();
    readonly disabled = input(false);
    readonly readonly = input(false);
    readonly ariaLabel = input<string | undefined>(undefined);
    readonly ariaRequired = input(false);
    readonly ariaInvalid = input(false);
    readonly ariaDescribedby = input<string | undefined>(undefined);
    readonly sortResultsBy = input<InferSignalProp<typeof S25MultiselectPopupComponent, "sortResultsBy">>("none");
    readonly sortResultsOrder = input<InferSignalProp<typeof S25MultiselectPopupComponent, "sortResultsOrder">>("asc");
    @Output() changed: EventEmitter<S25ItemI[]> = new EventEmitter();
    @Output() onDone: EventEmitter<S25ItemI[]> = new EventEmitter();
    @Output() close: EventEmitter<S25ItemI[]> = new EventEmitter();

    readonly popupComponent = viewChild(S25MultiselectPopupComponent);
    private searchCriteriaContext = inject(SearchCriteriaContext);
    private searchCriteriaDataUtil = inject(SearchCriteriaDataUtil);

    refresh = () => {
        try {
            this.cd.detectChanges();
            this.popupComponent()?.refresh();
        } catch (error: any) {}
    };

    async ngOnInit() {
        this.modelBean = this.modelBean || {};
        const onChange = this.modelBean.onChange;
        this.modelBean.usePopover = this.usePopover;
        this.modelBean.isSearchCriteria = true;
        this.modelBean.onChange = () => {
            this.changed.emit(this.modelBean.selectedItems);
            onChange?.();
        };
        const prevOnMatchingChange = this.modelBean.onMatchingChange;
        this.modelBean.onMatchingChange = (matching) => {
            prevOnMatchingChange?.(matching);
            this.matching.set(matching);
        };
        if (this.buttonText) this.modelBean.buttonText = this.buttonText;

        this.modelBean.singleSelect ??= this.singleSelect ?? false;
        if (this.allowedItemIds?.length) {
            this.modelBean.allowedItemIds = this.allowedItemIds.map((id) => Number(id));
        }

        await this.searchCriteriaDataUtil.searchCriteriaSetup(
            this.modelBean,
            this.searchCriteriaContext.Context[this.type],
            this.selectedItems ?? this.value(),
            this.elementRef.nativeElement,
            this.honorMatching,
            this.customFilterValue,
            this.useSecurity,
            this.eventTypeId,
        );

        await super.ngOnInit();
        this.cd.markForCheck();
    }

    onClose() {
        this.close.emit(this.modelBean.selectedItems);
        this.emitDone();
    }

    emitDone() {
        this.value.set([...(this.modelBean.selectedItems ?? [])]);
        this.matching.set(this.modelBean.matching);
        this.onDone.emit(this.modelBean.selectedItems);
    }
}
